import React, { useEffect, useState } from "react";
import * as Yup from "yup";
import styled from "@emotion/styled";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { yupResolver } from "@hookform/resolvers/yup";
import { Grid, MenuItem } from "@mui/material";
import { useForm, FormProvider } from "react-hook-form";
import SearchableAutoComplete from "../../../../components/SearchableAutoComplete";
import FilterReset from "../../../../components/FilterReset";
import { invoiceStatusOptions, convertedValue, formatDate } from "../../../../utils/common";
import ReactHookFormSelect from "../../../../components/ReactHookFormSelect";
import CustomTextField from "../../../../components/CustomTextField";
import { useDialog } from "../../../../contexts/DialogContext";
import CustomDatePicker from "../../../../components/CustomDatePicker";
import useTenant from "../../../../hooks/useTenant";

const currentPayload = {
  filters: [],
  pagination: {
    pageNumber: 1,
    pageSize: 100,
  },
  sortFields: [],
};

const paymentStatusOptions = [
  {
    label: "SUCCESS",
    value: "SUCCESS",
  },
  {
    label: "FAILURE",
    value: "FAILURE",
  },
  {
    label: "PENDING",
    value: "PENDING",
  },
  {
    label: "CANCELLED",
    value: "CANCELLED",
  },
];

function AdvanceFilter(props) {
  const { unSetDialog } = useDialog();
  const today = formatDate(new Date());

  const initialValues = {
    userName: "",
    paymentId: "",
    paymentGateway: "",
    orderId: "",
    tenantName: {},
    transDateStr: `${today} 00:00:00,${today} 23:59:59`,
    transDate: [new Date(), new Date()],
    paymentStatus: {},
  };

  const { fields, payload, setPayload, setFilterResetFunc, filterFormData, setFilterFormData } =
    props;

  const { autoSearchTenant, tenantAutoSearchResults, isLoadingAutoSearch } = useTenant();

  const [tenantPayload, setTenantPayload] = useState(currentPayload);
  const [tenantOptions, setTenantOptions] = useState([]);

  const [defaultTenant, setDefaultTenant] = useState({});
  const [defaultStatus, setDefaultStatus] = useState({});

  const { t } = useTranslation();
  const methods = useForm({
    defaultValues: filterFormData || initialValues,
  });

  const {
    reset,
    handleSubmit,
    formState: { errors },
    control,
    watch,
    setValue,
  } = methods;

  useEffect(() => {
    setFilterResetFunc(() => () => reset(initialValues));
  }, [reset]);

  const onSubmit = async (data) => {
    try {
      setFilterFormData(data);
      let filters = [];
      if (data.paymentId != null && Object.keys(data.paymentId).length != 0) {
        filters.push({
          fieldName: "paymentId",
          fieldValue: data.paymentId,
          op: "eq",
          dataType: "string",
          condition: "and",
          isJoin: false,
        });
      }
      if (data.paymentGateway != null && Object.keys(data.paymentGateway).length != 0) {
        filters.push({
          fieldName: "paymentGateway",
          fieldValue: data.paymentGateway,
          op: "eq",
          dataType: "string",
          condition: "and",
          isJoin: true,
          entityName: "PaymentGatewayEntity",
          attributeName: "paymentGateway",
        });
      }
      if (data.orderId != null && Object.keys(data.orderId).length != 0) {
        filters.push({
          fieldName: "orderId",
          fieldValue: data.orderId,
          op: "eq",
          dataType: "string",
          condition: "and",
          isJoin: false,
        });
      }
      if (data.userName != null && Object.keys(data.userName).length != 0) {
        filters.push({
          fieldName: "userName",
          fieldValue: data.userName,
          op: "like",
          dataType: "string",
          condition: "and",
          isJoin: true,
          entityName: "RadUserEntity",
          attributeName: "radUser",
        });
      }

      if (data.paymentStatus != null && Object.keys(data.paymentStatus).length != 0) {
        filters.push({
          fieldName: "paymentStatus",
          fieldValue: convertedValue(data.paymentStatus, "value").join(","),
          op: "eq",
          dataType: "string",
          condition: "and",
          isJoin: false,
        });
      }
      if (data.tenantName != null && Object.keys(data.tenantName).length != 0) {
        filters.push({
          fieldName: "tenantId",
          fieldValue: convertedValue(data.tenantName, "value").join(","),
          op: "eq",
          dataType: "int",
          condition: "and",
          isJoin: true,
          entityName: "TenantEntity",
          attributeName: "tenant",
        });
      }
      if (data.transDate != "" && data.transDate != null) {
        console.log(data.transDateStr, "transDateStr");
        filters.push({
          fieldName: "createdAt",
          fieldValue: data.transDateStr,
          op: "btn",
          dataType: "date",
          condition: "and",
          isJoin: false,
        });
      }
      setPayload({
        ...payload,
        filters: filters,
      });
      unSetDialog("advance_filter");
    } catch (err) {
      console.error(err, "errerrerrerr");
    }
  };

  const fetchTenant = async () => {
    await autoSearchTenant(tenantPayload);
  };
  useEffect(() => {
    fetchTenant();
  }, [tenantPayload]);

  useEffect(() => {
    console.log(tenantAutoSearchResults, "tenantAutoSearchResults");
    if (tenantAutoSearchResults.data.length !== 0) {
      setTenantOptions([...tenantOptions, ...tenantAutoSearchResults.data]);
    }
  }, [tenantAutoSearchResults]);

  return (
    <React.Fragment>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={2} pt={8}>
            <Grid item md={6} sm={6} xs={8}>
              <CustomTextField
                label={t("user_name")}
                name="userName"
                control={control}
                fullWidth
                variant="outlined"
                errors={errors}
              />
            </Grid>
            <Grid item md={6} sm={6} xs={8}>
              <CustomTextField
                label={t("order_id")}
                name="orderId"
                control={control}
                fullWidth
                variant="outlined"
                errors={errors}
              />
            </Grid>
          </Grid>
          <Grid container spacing={2} pt={8}>
            <Grid item md={6} sm={6} xs={8}>
              <SearchableAutoComplete
                control={control}
                data={tenantOptions}
                label={t("franchise_name")}
                payload={tenantPayload}
                setPayload={setTenantPayload}
                loading={isLoadingAutoSearch}
                name="tenantName"
                multiple={false}
                defaultValue={defaultTenant}
                hasNextPage={tenantAutoSearchResults.paging.hasNextPage}
              />
            </Grid>
            <Grid item md={6} sm={6} xs={8}>
              <CustomTextField
                label={t("gateway_name")}
                name="paymentGateway"
                control={control}
                fullWidth
                variant="outlined"
                errors={errors}
              />
            </Grid>
          </Grid>
          <Grid container spacing={2} pt={8}>
            <Grid item md={6} sm={6} xs={8}>
              <SearchableAutoComplete
                control={control}
                data={paymentStatusOptions}
                label="Payment Status"
                payload={null}
                setPayload={null}
                loading={false}
                name="paymentStatus"
                multiple={false}
                defaultValue={defaultStatus}
              />
            </Grid>
            <Grid item md={6} sm={6} xs={8}>
              <CustomDatePicker
                name="transDate"
                nameTemp="transDateStr"
                label="Transaction Date"
                range={true}
                control={control}
              />
            </Grid>
          </Grid>

          <Grid container spacing={2} pt={8}>
            <Grid item md={6} sm={6} xs={8}>
              <CustomTextField
                label={t("payment_id")}
                name="paymentId"
                control={control}
                fullWidth
                variant="outlined"
                errors={errors}
              />
            </Grid>
          </Grid>
          <FilterReset resetForm={() => reset(initialValues)} />
        </form>
      </FormProvider>
    </React.Fragment>
  );
}

export default AdvanceFilter;
